import { forwardRef } from "react";

import { cn } from "../../lib/utils.ts";

/**
 * Slack brand mark, inline SVG so we do not depend on a hosted asset.
 *
 * Colors are Slack's official brand palette. Keep them inline (vs Tailwind
 * `text-*` classes) so the icon renders correctly regardless of parent text
 * color, matching how `GoogleIcon` / `MicrosoftIcon` work in this folder.
 *
 * Props spread last, matching `TeamsIcon` and `WhatsAppIcon`, so a caller can
 * relabel the mark for its context. `TooltipTrigger asChild` also clones the
 * trigger's handlers, its `aria-describedby` and the Popper anchor ref onto
 * this element, and a component taking only `className` drops all three.
 */
export const SlackIcon = forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement>>(
  ({ className, ...props }, ref) => {
    return (
      <svg
        ref={ref}
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        className={cn(className)}
        aria-label="Slack"
        role="img"
        {...props}
      >
        <path
          fill="#E01E5A"
          d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.27 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.833 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z"
        />
        <path
          fill="#36C5F0"
          d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.52 2.522v2.52H8.834zm0 1.27a2.527 2.527 0 0 1 2.52 2.521 2.527 2.527 0 0 1-2.52 2.521H2.522A2.528 2.528 0 0 1 0 8.833a2.528 2.528 0 0 1 2.522-2.521h6.312z"
        />
        <path
          fill="#2EB67D"
          d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.52h-2.522v-2.52zm-1.272 0a2.527 2.527 0 0 1-2.52 2.52 2.527 2.527 0 0 1-2.521-2.52V2.522A2.528 2.528 0 0 1 15.165 0a2.528 2.528 0 0 1 2.521 2.522v6.312z"
        />
        <path
          fill="#ECB22E"
          d="M15.165 18.956a2.528 2.528 0 0 1 2.521 2.522A2.528 2.528 0 0 1 15.165 24a2.528 2.528 0 0 1-2.52-2.522v-2.522h2.52zm0-1.27a2.527 2.527 0 0 1-2.52-2.521 2.527 2.527 0 0 1 2.52-2.521h6.313A2.528 2.528 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.521h-6.313z"
        />
      </svg>
    );
  },
);
SlackIcon.displayName = "SlackIcon";
